/**
 * Discount Manager - Global discount code management for cart
 * Handles applying, removing, and validating discount codes across cart drawer and main cart
 *
 * @version 1.0.0
 * @requires Shopify Ajax Cart API
 */

class DiscountManager {
  constructor() {
    this.currentDiscounts = [];
    this.manuallyAppliedCodes = []; // Track codes applied by user through input
    this.isProcessing = false;
    this.init();
  }

  init() {
    try {
      const stored = localStorage.getItem('manuallyAppliedDiscountCodes');
      if (stored) {
        this.manuallyAppliedCodes = JSON.parse(stored);
      }
    } catch (e) {
      // Silent fail
    }

    this.loadCurrentDiscounts();
    this.initCheckoutInterception();
    this.setupDrawerCloseListener();

    document.addEventListener('cart:updated', () => {
      this.loadCurrentDiscounts();
    });
  }

  setupDrawerCloseListener() {
    document.addEventListener('click', (e) => {
      const isOverlay = e.target.classList.contains('side-panel-overlay');
      const isCloseButton = e.target.closest('.side-panel-close');

      if (isOverlay || isCloseButton) {
        const drawerInput = document.getElementById('discount-code-input-drawer');
        if (drawerInput && drawerInput.value) {
          drawerInput.value = '';
        }
      }
    });
  }

  saveManualCodes() {
    try {
      localStorage.setItem('manuallyAppliedDiscountCodes', JSON.stringify(this.manuallyAppliedCodes));
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * Load current discounts from cart
   */
  async loadCurrentDiscounts() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();

      if (!cart.items || cart.items.length === 0) {
        this.currentDiscounts = [];
        this.manuallyAppliedCodes = [];
        this.saveManualCodes();
        return;
      }

      const cartLevelDiscounts = cart.cart_level_discount_applications || [];
      this.currentDiscounts = cartLevelDiscounts.map(d => d.title.toUpperCase());

      let lineItemDiscounts = [];
      if (cart.items && cart.items.length > 0) {
        cart.items.forEach(item => {
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
            item.line_level_discount_allocations.forEach(discount => {
              const title = discount.discount_application?.title;
              if (title) {
                lineItemDiscounts.push(title.toUpperCase());
              }
            });
          }
        });
      }

      const allActiveDiscounts = [...new Set([...this.currentDiscounts, ...lineItemDiscounts])];

      const beforeCleanup = [...this.manuallyAppliedCodes];
      this.manuallyAppliedCodes = this.manuallyAppliedCodes.filter(code => {
        return allActiveDiscounts.includes(code.toUpperCase());
      });

      this.saveManualCodes();

    } catch (error) {
      // Silent fail
    }
  }

  async applyDiscountFromInput(inputId) {
    const input = document.getElementById(inputId);
    if (!input) {
      return;
    }

    const applyButton = input.parentElement?.querySelector('.discount-apply-btn');
    const code = input.value.trim();

    if (!code) {
      this.showError('Please enter a discount code', inputId);
      return;
    }

    const codeUpper = code.toUpperCase();

    if (this.manuallyAppliedCodes.some(c => c.toUpperCase() === codeUpper)) {
      this.showError('This discount code is already applied', inputId);
      return;
    }

    if (applyButton) {
      applyButton.classList.add('loading');
      applyButton.disabled = true;
      applyButton.setAttribute('aria-busy', 'true');
    }

    if (applyButton) {
      applyButton.dataset.inputId = inputId;
    }

    await this.applyDiscount(codeUpper, applyButton || input);
  }

  async applyDiscount(code, triggerElement = null) {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.showLoading(triggerElement, true);
    this.hideError();

    try {
      if (!code || !code.trim()) {
        const inputId = triggerElement?.dataset?.inputId || triggerElement?.id;
        this.showError('Please enter a valid discount code', inputId);
        return;
      }

      const codeUpper = code.trim().toUpperCase();

      // Save current state before applying (for rollback if needed)
      const previousDiscounts = [...this.currentDiscounts];
      const previousManualCodes = [...this.manuallyAppliedCodes];

      // Prepare discount codes: include ALL manually applied codes plus new code
      // This ensures we don't lose product-level discounts when applying order-level discounts
      const allCodes = [...new Set([...this.manuallyAppliedCodes, codeUpper])];
      const discountCodes = allCodes.join(',');


      // Apply discount using Shopify Ajax Cart API
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: discountCodes,
          sections: 'cart-drawer,cart-bubble,main-cart'
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.description || result.message) {
        const errorMsg = result.description || result.message || 'Invalid discount code';
        const inputId = triggerElement?.dataset?.inputId || triggerElement?.id;
        this.showError(errorMsg, inputId);
        return;
      }

      // CRITICAL: Verify discount was actually applied by fetching fresh cart state
      // Don't assume success - check if the discount exists in cart
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!cartResponse.ok) throw new Error(`HTTP ${cartResponse.status}`);
      const updatedCart = await cartResponse.json();

      // Extract applied discounts from cart level
      const cartLevelDiscounts = updatedCart.cart_level_discount_applications || [];
      const cartLevelTitles = cartLevelDiscounts.map(d => d.title.toUpperCase());

      // Also check line item level discounts (product-specific discounts)
      let lineItemDiscountTitles = [];
      if (updatedCart.items && updatedCart.items.length > 0) {
        updatedCart.items.forEach(item => {
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
            item.line_level_discount_allocations.forEach(discount => {
              const title = discount.discount_application?.title;
              if (title) {
                lineItemDiscountTitles.push(title.toUpperCase());
              }
            });
          }
        });
      }

      // Combine both cart-level and line-item level discounts
      const allAppliedDiscountTitles = [...new Set([...cartLevelTitles, ...lineItemDiscountTitles])];


      // Check if the discount code was actually applied anywhere
      const discountApplied = allAppliedDiscountTitles.includes(codeUpper);

      if (!discountApplied) {
        // Discount was not applied anywhere

        const inputId = triggerElement?.dataset?.inputId || triggerElement?.id;

        // Check if there's an existing discount (might be better than the new one)
        if (allAppliedDiscountTitles.length > 0 && previousDiscounts.length > 0) {
          // If there's still a discount applied and we had one before,
          // the new code might be valid but overridden by a better discount
          this.showError('A better discount is already applied to your cart', inputId);
        } else {
          // Otherwise, the discount code is invalid
          this.showError('Invalid discount code', inputId);
        }
        return;
      }

      // Success - discount was applied (either cart-level or line-item level)
      // Update current discounts to track cart-level ones
      this.currentDiscounts = cartLevelTitles;

      // Check if any previous discounts were removed by Shopify (replaced by new one)
      const removedDiscounts = previousManualCodes.filter(prevCode =>
        !allAppliedDiscountTitles.includes(prevCode.toUpperCase())
      );

      // Remove the replaced discounts from manually applied codes
      if (removedDiscounts.length > 0) {

        // Remove replaced codes from our tracking
        removedDiscounts.forEach(removedCode => {
          const index = this.manuallyAppliedCodes.findIndex(c =>
            c.toUpperCase() === removedCode.toUpperCase()
          );
          if (index > -1) {
            this.manuallyAppliedCodes.splice(index, 1);
          }
        });

        // CRITICAL FIX: Add the new code to manually applied codes
        // This was missing before, causing the new code to not be tracked
        if (!this.manuallyAppliedCodes.some(c => c.toUpperCase() === codeUpper)) {
          this.manuallyAppliedCodes.push(codeUpper);
        }

        // Calculate discount comparison
        // Get total savings from replaced discounts vs new discount
        const totalDiscount = updatedCart.total_discount || 0;
        const originalPrice = updatedCart.items_subtotal_price || updatedCart.total_price + totalDiscount;

        // Show user-friendly message
        let message;
        if (removedDiscounts.length === 1) {
          message = `Discount "${codeUpper}" applied! (Replaced "${removedDiscounts[0]}" with a better discount)`;
        } else {
          message = `Discount "${codeUpper}" applied! (Replaced ${removedDiscounts.length} previous discounts with a better one)`;
        }

        // Save state
        this.saveManualCodes();

        // Refresh cart
        await new Promise(resolve => setTimeout(resolve, 300));
        const sectionsResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sections: 'cart-drawer,cart-bubble,main-cart'
          })
        });

        if (!sectionsResponse.ok) throw new Error(`HTTP ${sectionsResponse.status}`);
        const freshSections = await sectionsResponse.json();
        this.refreshCart(freshSections);

        // Clear input
        if (triggerElement && triggerElement.tagName === 'INPUT') {
          triggerElement.value = '';
        }

        // Show informative success message
        const inputId = triggerElement?.dataset?.inputId || triggerElement?.id;
        this.showMessage(message, 'success', inputId);

        return;
      }

      // Add new code to manually applied codes (if not already there)
      if (!this.manuallyAppliedCodes.some(c => c.toUpperCase() === codeUpper)) {
        this.manuallyAppliedCodes.push(codeUpper);
        this.saveManualCodes();
      }

      // Wait a brief moment for Shopify to finish processing the discount
      // This ensures prices are fully calculated before we refresh the UI
      await new Promise(resolve => setTimeout(resolve, 300));

      // Fetch fresh sections with updated discount prices
      const sectionsResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sections: 'cart-drawer,cart-bubble,main-cart'
        })
      });

      if (!sectionsResponse.ok) throw new Error(`HTTP ${sectionsResponse.status}`);
      const freshSections = await sectionsResponse.json();
      this.refreshCart(freshSections);

      // Clear input if exists
      if (triggerElement && triggerElement.tagName === 'INPUT') {
        triggerElement.value = '';
      } else if (triggerElement && triggerElement.id) {
        // Only try to find related input if button has an ID
        const inputId = triggerElement.id.replace('-button', '');
        if (inputId) {
          const input = document.getElementById(inputId);
          if (input) input.value = '';
        }
      }

      // Show success message below input
      let successMessage = `Discount "${codeUpper}" applied successfully!`;
      if (removedDiscounts.length > 0) {
        // If previous discounts were replaced, inform the user
        successMessage = `Discount "${codeUpper}" applied! (Previous discount${removedDiscounts.length > 1 ? 's' : ''} replaced)`;
      }

      const inputId = triggerElement?.dataset?.inputId || triggerElement?.id;
      this.showMessage(successMessage, 'success', inputId);

    } catch (error) {
      const inputId = triggerElement?.dataset?.inputId || triggerElement?.id;
      this.showError('Failed to apply discount. Please try again.', inputId);
    } finally {
      this.isProcessing = false;
      this.showLoading(triggerElement, false);
    }
  }

  /**
   * Remove a discount code from the cart
   * @param {string} code - Discount code to remove
   */
  async removeDiscount(code) {
    if (this.isProcessing) return;

    this.isProcessing = true;

    try {
      const codeUpper = code.toUpperCase();

      // Save current state for rollback
      const previousManualCodes = [...this.manuallyAppliedCodes];

      // Remove code from manually applied codes first
      this.manuallyAppliedCodes = this.manuallyAppliedCodes.filter(c =>
        c.toUpperCase() !== codeUpper
      );

      // Build discount string from remaining manually applied codes
      const discountCodes = this.manuallyAppliedCodes.join(',');

      // Update cart with remaining discounts
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: discountCodes || '', // Empty string to clear all discounts
          sections: 'cart-drawer,cart-bubble,main-cart'
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.description || result.message) {
        this.showNotification('Failed to remove discount', 'error');
        // Restore the code if removal failed
        this.manuallyAppliedCodes = previousManualCodes;
        return;
      }

      // Verify removal by fetching fresh cart state
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!cartResponse.ok) throw new Error(`HTTP ${cartResponse.status}`);
      const updatedCart = await cartResponse.json();

      // Extract current discounts
      const cartLevelTitles = (updatedCart.cart_level_discount_applications || [])
        .map(d => d.title.toUpperCase());

      let lineItemDiscountTitles = [];
      if (updatedCart.items && updatedCart.items.length > 0) {
        updatedCart.items.forEach(item => {
          if (item.line_level_discount_allocations) {
            item.line_level_discount_allocations.forEach(discount => {
              const title = discount.discount_application?.title;
              if (title) lineItemDiscountTitles.push(title.toUpperCase());
            });
          }
        });
      }

      const allAppliedDiscounts = [...new Set([...cartLevelTitles, ...lineItemDiscountTitles])];

      // Check if discount was actually removed
      if (allAppliedDiscounts.includes(codeUpper)) {
        this.showNotification('Failed to remove discount', 'error');
        // Restore the code
        this.manuallyAppliedCodes = previousManualCodes;
        return;
      }

      // Success - save updated list
      this.saveManualCodes();
      this.currentDiscounts = cartLevelTitles;


      // Wait a brief moment for Shopify to finish processing
      await new Promise(resolve => setTimeout(resolve, 300));

      // Fetch fresh sections with updated discount prices
      const sectionsResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sections: 'cart-drawer,cart-bubble,main-cart'
        })
      });

      if (!sectionsResponse.ok) throw new Error(`HTTP ${sectionsResponse.status}`);
      const freshSections = await sectionsResponse.json();
      this.refreshCart(freshSections);
      this.showNotification(`Discount "${codeUpper}" removed`, 'success');

    } catch (error) {
      this.showNotification('Failed to remove discount. Please try again.', 'error');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Refresh cart UI with new data
   * @param {Object} cartData - Cart data from Shopify API response
   */
  refreshCart(cartData) {
    if (!cartData || !cartData.sections) return;

    // Save accordion open states before update
    const accordionStates = {};
    document.querySelectorAll('.discount-accordion').forEach(accordion => {
      const id = accordion.id;
      if (id) {
        accordionStates[id] = accordion.classList.contains('open');
      }
    });

    // Update cart drawer
    if (cartData.sections['cart-drawer']) {
      const cartDrawerElement = document.getElementById('Cart-Drawer');
      if (cartDrawerElement) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cartData.sections['cart-drawer'], 'text/html');
        const newContent = doc.querySelector('.side-panel-inner');
        const currentContent = cartDrawerElement.querySelector('.side-panel-inner');
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }
      }
    }

    // Update cart bubble
    if (cartData.sections['cart-bubble']) {
      const bubbleElement = document.getElementById('cart-drawer-toggle');
      if (bubbleElement) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cartData.sections['cart-bubble'], 'text/html');
        const newBubble = doc.querySelector('.thb-item-count');
        const currentBubble = bubbleElement.querySelector('.thb-item-count');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      }
    }

    // Update main cart page if present
    if (cartData.sections['main-cart']) {
      const mainCartElement = document.getElementById('Cart');
      if (mainCartElement) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cartData.sections['main-cart'], 'text/html');

        // Update the entire cart collaterals section
        const newCollaterals = doc.querySelector('.thb-cart-collaterals');
        const currentCollaterals = mainCartElement.querySelector('.thb-cart-collaterals');
        if (newCollaterals && currentCollaterals) {
          currentCollaterals.innerHTML = newCollaterals.innerHTML;
        }

        // Also update cart items if needed
        const newCartItems = doc.querySelector('.thb-cart-form--form');
        const currentCartItems = mainCartElement.querySelector('.thb-cart-form--form');
        if (newCartItems && currentCartItems) {
          currentCartItems.innerHTML = newCartItems.innerHTML;
        }
      }
    }

    // Re-initialize accordion after update
    if (window.initDiscountAccordion) {
      window.initDiscountAccordion();
    }

    // Restore accordion open states
    Object.keys(accordionStates).forEach(id => {
      if (accordionStates[id]) {
        const accordion = document.getElementById(id);
        if (accordion) {
          const content = accordion.querySelector('.discount-accordion-content');
          const toggleBtn = accordion.querySelector('.discount-toggle-btn');
          if (content && toggleBtn) {
            accordion.classList.add('open');
            content.style.display = 'block';
            toggleBtn.setAttribute('aria-expanded', 'true');
          }
        }
      }
    });

    // Trigger custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: cartData }
    }));

    // Filter discount tags to only show manually applied codes
    this.filterDiscountTags();

    // Re-initialize checkout interception after DOM update
    setTimeout(() => {
      this.initCheckoutInterception();
    }, 100);
  }

  /**
   * Render discount tags dynamically based on manually applied codes
   */
  filterDiscountTags() {

    const tagContainers = document.querySelectorAll('.applied-discount-tags');

    if (tagContainers.length === 0) {
      return;
    }


    tagContainers.forEach((container, index) => {
      // Clear existing content
      container.innerHTML = '';

      // If no manually applied codes, hide container and return
      if (this.manuallyAppliedCodes.length === 0) {
        container.style.display = 'none';
        return;
      }


      // Create tags for each manually applied discount
      this.manuallyAppliedCodes.forEach(code => {
        const tag = document.createElement('span');
        tag.className = 'discount-tag';

        // Create discount tag HTML with icon
        tag.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" focusable="false" aria-hidden="true" class="discount-tag-icon">
            <g stroke-linejoin="round" clip-path="url(#discount_svg__a)">
              <path stroke-linecap="round" d="M10.5 1.5H7.93a2.5 2.5 0 0 0-1.8.766L1.52 7.052a1.5 1.5 0 0 0 .02 2.101l3.48 3.48a1.25 1.25 0 0 0 1.75.016l5.116-4.926a2 2 0 0 0 .613-1.441V3.5a2 2 0 0 0-2-2"></path>
              <circle cx="9.5" cy="4.5" r="0.563" stroke-linecap="round"></circle>
              <path d="M9.49 4.49h.02v.02h-.02z"></path>
            </g>
            <defs>
              <clipPath id="discount_svg__a">
                <path fill="#fff" d="M0 0h14v14H0z"></path>
              </clipPath>
            </defs>
          </svg>
          ${code}
          <button
            type="button"
            class="discount-tag-remove"
            aria-label="Remove ${code}"
          >×</button>
        `;

        // Add remove functionality
        const removeBtn = tag.querySelector('.discount-tag-remove');
        removeBtn.addEventListener('click', () => {
          this.removeDiscount(code);
        });

        container.appendChild(tag);
      });

      // Show container
      container.style.display = 'flex';
    });

  }

  /**
   * Show loading state on trigger element
   * @param {HTMLElement} element - Element to show loading state on
   * @param {boolean} isLoading - Whether to show or hide loading state
   */
  showLoading(element, isLoading) {
    if (!element) return;

    if (isLoading) {
      // Skip if already in loading state
      if (element.classList.contains('loading')) return;

      element.classList.add('loading');
      element.disabled = true;
      if (element.tagName === 'BUTTON') {
        element.setAttribute('aria-busy', 'true');
        // Don't change button text - CSS will show spinner instead
      }
    } else {
      element.classList.remove('loading');
      element.disabled = false;
      if (element.tagName === 'BUTTON') {
        element.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Show message below input field
   * @param {string} message - Message to display
   * @param {string} type - Message type ('success' or 'error')
   * @param {string} inputId - ID of the input field to show message for
   */
  showMessage(message, type = 'error', inputId = null) {
    let messageContainer;
    let wrapper;
    let input;
    let applyButton;

    if (inputId) {
      input = document.getElementById(inputId);
      if (input) {
        // Get the wrapper
        wrapper = input.closest('.discount-input-wrapper');
        if (wrapper) {
          // Add state class to wrapper for visual feedback
          wrapper.classList.remove('input-success', 'input-error');
          wrapper.classList.add(type === 'success' ? 'input-success' : 'input-error');

          // Add appropriate class based on type
          wrapper.classList.remove('error', 'success');
          wrapper.classList.add(type);

          // Find apply button and change text if success
          applyButton = wrapper.querySelector('.discount-apply-btn');
          if (applyButton && type === 'success') {
            applyButton.dataset.originalText = applyButton.textContent;
            applyButton.textContent = 'Applied';
          }

          // Find message container
          messageContainer = wrapper.parentElement.querySelector('.discount-message');
        }
      }
    }

    // Fallback: find any message container
    if (!messageContainer) {
      messageContainer = document.getElementById('discount-message-drawer')
        || document.getElementById('discount-message-main');
    }

    if (messageContainer) {
      messageContainer.textContent = message;
      messageContainer.className = `discount-message ${type}`;
      messageContainer.style.display = 'block';

      // Auto-hide after 2 seconds
      setTimeout(() => {
        this.hideMessage();
      }, 2000);
    }
  }

  /**
   * Hide message
   */
  hideMessage() {
    const messageContainers = document.querySelectorAll('[id^="discount-message-"]');
    messageContainers.forEach(container => {
      container.style.display = 'none';
      container.textContent = '';
      container.className = 'discount-message';
    });

    // Remove status classes from wrappers
    const wrappers = document.querySelectorAll('.discount-input-wrapper.error, .discount-input-wrapper.success, .discount-input-wrapper.input-success, .discount-input-wrapper.input-error');
    wrappers.forEach(wrapper => {
      wrapper.classList.remove('error', 'success', 'input-success', 'input-error');

      // Restore button text
      const applyButton = wrapper.querySelector('.discount-apply-btn');
      if (applyButton && applyButton.dataset.originalText) {
        applyButton.textContent = applyButton.dataset.originalText;
        delete applyButton.dataset.originalText;
      }
    });
  }

  /**
   * Show error message (kept for backward compatibility)
   * @param {string} message - Error message to display
   * @param {string} inputId - ID of the input field to show error for
   */
  showError(message, inputId = null) {
    this.showMessage(message, 'error', inputId);
  }

  /**
   * Hide error message (kept for backward compatibility)
   */
  hideError() {
    this.hideMessage();
  }

  /**
   * Show notification toast
   * @param {string} message - Notification message
   * @param {string} type - Notification type ('success' or 'error')
   */
  showNotification(message, type = 'success') {
    // Try to use existing notification system
    const existingNotification = document.querySelector('.promo-discount-notification');

    if (existingNotification) {
      // Use the existing promo notification system
      const messageEl = existingNotification.querySelector('.promo-notification-message');
      const iconEl = existingNotification.querySelector('.promo-notification-icon');

      if (messageEl) messageEl.textContent = message;

      if (iconEl) {
        const icons = {
          success: '<svg viewBox="0 0 20 20"><path fill="currentColor" d="M8.33333 15L3.33333 10L4.74167 8.58333L8.33333 12.1667L15.2583 5.25L16.6667 6.66667L8.33333 15Z"/></svg>',
          error: '<svg viewBox="0 0 20 20"><path fill="currentColor" d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"/></svg>'
        };
        iconEl.innerHTML = icons[type] || '';
      }

      existingNotification.className = `promo-discount-notification ${type}-type`;
      existingNotification.classList.add('show');

      setTimeout(() => {
        existingNotification.classList.remove('show');
      }, 4500);
    } else {
      // Fallback: Create simple notification
      this.createSimpleNotification(message, type);
    }
  }

  /**
   * Create a simple notification toast (fallback)
   * @param {string} message - Notification message
   * @param {string} type - Notification type
   */
  createSimpleNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `discount-notification ${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : '#ef4444'};
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 99999;
      font-size: 14px;
      font-weight: 500;
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  }

  /**
   * Initialize checkout interception to pass all discount codes
   * This ensures both product-level and order-level discounts are passed to checkout
   */
  initCheckoutInterception() {
    // Intercept main cart checkout button
    const mainCheckoutButton = document.getElementById('main-checkout-button');
    if (mainCheckoutButton && !mainCheckoutButton.dataset.intercepted) {
      mainCheckoutButton.dataset.intercepted = 'true';
      mainCheckoutButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleCheckout();
      });
    }

    // Intercept cart drawer checkout form
    const drawerCheckoutForm = document.getElementById('drawer-checkout-form');
    if (drawerCheckoutForm && !drawerCheckoutForm.dataset.intercepted) {
      drawerCheckoutForm.dataset.intercepted = 'true';
      drawerCheckoutForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleCheckout();
      });
    }
  }

  /**
   * Handle checkout by passing all manually applied discount codes
   */
  handleCheckout() {
    // Get all manually applied discount codes
    if (this.manuallyAppliedCodes.length > 0) {
      // Join all codes with comma
      const allDiscounts = this.manuallyAppliedCodes.join(',');

      // Build checkout URL with discount parameter
      const checkoutUrl = `${window.Shopify.routes.root}checkout?discount=${encodeURIComponent(allDiscounts)}`;


      // Redirect to checkout
      window.location.href = checkoutUrl;
    } else {
      // No discounts, proceed to checkout normally
      window.location.href = `${window.Shopify.routes.root}checkout`;
    }
  }
}

// Initialize DiscountManager globally
window.DiscountManager = new DiscountManager();

// Filter tags on initial load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.DiscountManager.filterDiscountTags();
    window.DiscountManager.initCheckoutInterception();
  });
} else {
  window.DiscountManager.filterDiscountTags();
  window.DiscountManager.initCheckoutInterception();
}

// ==================================================
// Discount Accordion Toggle Functionality
// ==================================================

/**
 * Initialize discount accordion toggle functionality using event delegation
 */
function initDiscountAccordion() {
  // Mark that we've initialized
  if (window._discountAccordionInitialized) return;
  window._discountAccordionInitialized = true;

  // Use event delegation on document to handle clicks
  document.addEventListener('click', (e) => {
    // Check if clicked element is within accordion header
    const header = e.target.closest('.discount-accordion-header');
    if (!header) return;

    const accordion = header.closest('.discount-accordion');
    if (!accordion) return;

    const toggleBtn = accordion.querySelector('.discount-toggle-btn');
    const content = accordion.querySelector('.discount-accordion-content');

    if (!toggleBtn || !content) return;

    e.preventDefault();
    e.stopPropagation();

    const isOpen = accordion.classList.contains('open');

    if (isOpen) {
      // Close accordion
      accordion.classList.remove('open');
      content.style.display = 'none';
      toggleBtn.setAttribute('aria-expanded', 'false');
    } else {
      // Open accordion
      accordion.classList.add('open');
      content.style.display = 'block';
      toggleBtn.setAttribute('aria-expanded', 'true');

      // Focus on input when opening
      setTimeout(() => {
        const input = content.querySelector('.discount-code-input');
        if (input) input.focus();
      }, 100);
    }
  });
}

// Add keyboard support for Enter key on discount inputs
document.addEventListener('keypress', (e) => {
  if (e.target.classList.contains('discount-code-input') && e.key === 'Enter') {
    e.preventDefault();
    if (window.DiscountManager) {
      window.DiscountManager.applyDiscountFromInput(e.target.id);
    }
  }
});

// Initialize accordion on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountAccordion);
} else {
  initDiscountAccordion();
}

// Re-initialize accordion after cart updates (for AJAX cart refreshes)
document.addEventListener('cart:updated', () => {
  setTimeout(initDiscountAccordion, 100);
});

// Export for potential external use
window.initDiscountAccordion = initDiscountAccordion;
